import PortalStudentProfile from '@classes/StudentProfile'
import Select from '@components/Select'
import useCTPermission from '@hooks/useCTPermission'
import { StoreProps } from '@interfaces/StoreState'
import { ProgramSelectorProp } from '@layouts/Dashboard/HeroBlock/interface'
import { ProgramsWrapper } from '@layouts/Dashboard/HeroBlock/style'
import {
  updateDashboardActiveMaterial,
  updateDashboardActiveProgram,
  updateDashboardActiveRegistration,
} from '@redux/actions/dashboard'
import { CURRICULUM_TEST_STATUS, REG_STATUS } from '@utils/constants'
import { getQueryParams, decodeDDUniqueKey } from '@utils/helpers'
import { getStoredMaterials, getStoredPrograms, getStoredRegistrations } from '@utils/helpers/material'
import { useRouter } from 'next/router'
import React, { useEffect, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'

const RegStatusInOrder = [
  REG_STATUS.PENDING_START,
  REG_STATUS.ACTIVE,
  REG_STATUS.COMPLETED,
  REG_STATUS.EXPIRED,
  REG_STATUS.PENDING_START,
  REG_STATUS.CANCELLED,
  REG_STATUS.DRAFT,
  REG_STATUS.IN_PROGRESS,
  REG_STATUS.PAUSED,
  REG_STATUS.EXPIRED,
]

const ProgramSelector: React.FC<ProgramSelectorProp> = ({
  hide,
  defaultProgram,
  bgOn,
  StudentProfile,
  label = '',
  showCT,
  LearningPaths,
  setShowSelector = () => {},
}) => {
  const router = useRouter()
  const curriculumTestId = getQueryParams('dd')
  const { activeRegistration, activeProgram, activeMaterial, Profile, userPermissions } = useSelector(
    ({ userProfile, dashboard, permissions }: StoreProps) => ({
      Profile: StudentProfile || new PortalStudentProfile(userProfile?.info?.StudentProfile?.StudentProfileInfo),
      activeRegistration: dashboard.activeRegistration,
      activeProgram: dashboard.activeProgram,
      activeMaterial: dashboard.activeMaterial,
      userPermissions: permissions.permissions,
    })
  )
  const ISCFlag = userPermissions['flag::industrySpecificContent']?.show
  const { getPermissionByProgramType } = useCTPermission()

  let materialsDD2 = showCT ? Profile.LPMaterialDropDown2 : Profile.MaterialDropDown2

  const dispatch = useDispatch()
  const opt = localStorage.getItem('activeOPT')
  const optRegId = localStorage.getItem('activeOPTRegId')

  const findMaterialFromDD = (
    condition: (decoded: ReturnType<typeof decodeDDUniqueKey>) => boolean,
    includeSuboptions = false
  ) => {
    for (const material of materialsDD2) {
      const decoded = decodeDDUniqueKey(material.value)
      if (condition(decoded)) return decoded

      if (includeSuboptions && material.suboptions) {
        for (const suboption of material.suboptions) {
          const suboptionDecoded = decodeDDUniqueKey(suboption.value)
          if (condition(suboptionDecoded)) return suboptionDecoded
        }
      }
    }
    return {}
  }

  const getOptDetails = () =>
    findMaterialFromDD(
      (decoded) => decoded.registrationId === optRegId && (decoded.programId === opt || decoded.materialLPID == opt)
    )

  const getCurriculumTestDetails = () => findMaterialFromDD((decoded) => decoded.materialLPID == curriculumTestId, true)

  const getDefaultValues = () => {
    const defaultValues = { registrationId: '', programId: '', materialLPID: '' }

    const currentActiveOption = findMaterialFromDD(
      (decoded) =>
        decoded.registrationId == activeRegistration &&
        decoded.programId == activeProgram &&
        decoded.materialLPID == activeMaterial,
      showCT
    )

    const firstValidDDOption = decodeDDUniqueKey(
      materialsDD2?.filter((material) => !material.value.includes('undefined'))?.[0]?.value
    )

    if (opt) return { ...defaultValues, ...getOptDetails() }
    if (curriculumTestId) return { ...defaultValues, ...getCurriculumTestDetails() }
    if (Object.values(currentActiveOption).length && Object.values(currentActiveOption).every(Boolean))
      return { ...defaultValues, ...currentActiveOption }
    if (Object.values(firstValidDDOption).length && Object.values(firstValidDDOption).every(Boolean))
      return { ...defaultValues, ...firstValidDDOption }

    return {
      registrationId: defaultProgram ?? '',
      programId: defaultProgram ?? '',
      materialLPID: defaultProgram ?? '',
    }
  }

  const [selectedRegistration, setSelectedRegistration] = useState<string | undefined>(() => {
    const defaultRegistration = getDefaultValues().registrationId
    return defaultRegistration
  })
  const [selectedProgram, setSelectedProgram] = useState<string | undefined>(() => {
    const defaultProgram = getDefaultValues().programId
    return defaultProgram
  })
  const [selectedMaterial2, setSelectedMaterial2] = useState<string | undefined>(() => {
    const defaultMaterial = getDefaultValues().materialLPID

    const material = LearningPaths?.getLearningPathById(defaultMaterial)
    if (material?.IsCTMaterial) {
      const permission = getPermissionByProgramType(material?.Material?.ProgramType)
      if (permission) {
        return defaultMaterial
      }
      return material.MaterialCore?.LPID
    }
    return defaultMaterial
  })

  const [selectedDDValue, setSelectedDDValue] = useState(() => {
    return `${getDefaultValues().registrationId}|${getDefaultValues().programId}|${getDefaultValues().materialLPID}`
  })

  useEffect(() => {
    const activeMatDetail = ISCFlag
      ? findMaterialFromDD(
          (decoded) =>
            decoded.registrationId == activeRegistration &&
            decoded.programId == activeProgram &&
            decoded.materialLPID == activeMaterial
        )
      : { registrationId: activeRegistration, programId: activeProgram, materialLPID: activeMaterial }

    if (
      !(Object.values(activeMatDetail).length && Object.values(activeMatDetail).every(Boolean)) &&
      materialsDD2.length
    ) {
      dispatch(updateDashboardActiveRegistration(selectedRegistration ?? ''))
      dispatch(updateDashboardActiveProgram(selectedProgram ?? ''))
      dispatch(updateDashboardActiveMaterial(selectedMaterial2 ?? ''))
    }
  }, [])

  useEffect(() => {
    if (curriculumTestId) {
      const curriculamTestDetails = getCurriculumTestDetails()
      setSelectedMaterial2(curriculamTestDetails.materialLPID)
      setSelectedProgram(curriculamTestDetails.programId)
      setSelectedRegistration(curriculamTestDetails.registrationId)

      dispatch(updateDashboardActiveRegistration(curriculamTestDetails.registrationId ?? ''))
      dispatch(updateDashboardActiveProgram(curriculamTestDetails.programId ?? ''))
      dispatch(updateDashboardActiveMaterial(curriculamTestDetails.materialLPID ?? ''))

      router.push(`/learning-path`)
    }
  }, [curriculumTestId])

  useEffect(() => {
    if (hide || (materialsDD2.length < 2 && !materialsDD2.find((dd) => dd.suboptions?.length > 1))) {
      setShowSelector(false)
    }
  }, [materialsDD2])

  useEffect(() => {
    if (selectedMaterial2 === localStorage.getItem('activeOPT')) {
      localStorage.removeItem('activeOPT')
      localStorage.removeItem('activeOPTRegId')
    }
  }, [selectedMaterial2])

  if (hide || (materialsDD2.length < 2 && !materialsDD2.find((dd) => dd.suboptions?.length > 1))) {
    return null
  }

  const handleMaterialChange = (uniqueKey: string) => {
    const { registrationId, programId, materialLPID } = decodeDDUniqueKey(uniqueKey)

    if (!registrationId || !materialLPID || !programId) return

    setSelectedDDValue(uniqueKey)

    // Update localStorage
    const storedRegistrations = getStoredRegistrations()
    const storedPrograms = getStoredPrograms()
    const storedMaterials = getStoredMaterials()

    storedRegistrations[Profile.SFId] = registrationId
    storedPrograms[Profile.SFId] = programId
    storedMaterials[Profile.SFId] = materialLPID

    localStorage.setItem('portal-selected-registration', JSON.stringify(storedRegistrations))
    localStorage.setItem('portal-selected-program', JSON.stringify(storedPrograms))
    localStorage.setItem('portal-selected-material', JSON.stringify(storedMaterials))

    setSelectedRegistration(registrationId)
    setSelectedProgram(programId)
    setSelectedMaterial2(materialLPID)

    // Update Redux store
    dispatch(updateDashboardActiveRegistration(registrationId))
    dispatch(updateDashboardActiveProgram(programId))
    dispatch(updateDashboardActiveMaterial(materialLPID))
  }

  return (
    <ProgramsWrapper bgOn={bgOn}>
      {!label && (
        <span>
          <FormattedMessage id="Select your course:" />
        </span>
      )}
      <div>
        <Select
          label={label}
          name="Program"
          value={selectedDDValue}
          onChange={handleMaterialChange}
          options={materialsDD2}
          disableMargin
          responsive
        />
      </div>
    </ProgramsWrapper>
  )
}

export default ProgramSelector
